/**
 * Sticky Cards List
 * Base stacking/peek effect comes from staggered CSS `position: sticky`
 * offsets per card (each card sticks a little lower than the previous one,
 * so earlier cards remain visible as slivers behind it).
 *
 * This script adds the "receding" depth effect on top of that: once a card
 * becomes stuck, we figure out how many cards have since stacked on top of
 * it ("depth") and scale/fade it down a notch per layer of depth. Because
 * a stuck sticky element's rect.top never moves past its own `top` value,
 * depth has to be derived by comparing all items' stuck state each frame
 * rather than by tracking an individual item's scroll distance.
 */
(function () {
  'use strict';

  const SELECTOR_LIST = '[data-sticky-cards-list]';
  const SELECTOR_ITEM = '[data-sticky-item]';
  const SELECTOR_CARD = '[data-sticky-card]';

  const SCALE_STEP = 0.090; // scale reduction per layer of depth
  const OPACITY_STEP = 0.14; // opacity reduction per layer of depth
  const MIN_SCALE = 0.82;
  const MIN_OPACITY = 0.35;

  class StickyCardsList {
    constructor(listEl) {
      this.listEl = listEl;
      this.items = Array.from(listEl.querySelectorAll(SELECTOR_ITEM));
      if (!this.items.length) return;

      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.update();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(this.update);
    }

    update() {
      // Find the "active" (front-most) card: the last one that has already
      // reached and is holding at its own sticky top offset.
      let activeIndex = 0;

      this.items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const stickyTop = parseFloat(getComputedStyle(item).top) || 0;

        if (rect.top <= stickyTop + 1) {
          activeIndex = i;
        }
      });

      this.items.forEach((item, i) => {
        const card = item.querySelector(SELECTOR_CARD);
        if (!card) return;

        const depth = activeIndex - i; // 0 = active/front-most card

        if (depth <= 0) {
          card.style.transform = 'scale(1)';
          card.style.opacity = '1';
        } else {
          const scale = Math.max(1 - depth * SCALE_STEP, MIN_SCALE);
          const opacity = Math.max(1 - depth * OPACITY_STEP, MIN_OPACITY);
          card.style.transform = `scale(${scale.toFixed(3)})`;
          card.style.opacity = opacity.toFixed(3);
        }
      });

      this.ticking = false;
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_LIST).forEach((listEl) => {
      if (listEl.__stickyCardsListInstance) return;
      listEl.__stickyCardsListInstance = new StickyCardsList(listEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    const listEl = event.target.querySelector(SELECTOR_LIST);
    if (listEl) {
      listEl.__stickyCardsListInstance = new StickyCardsList(listEl);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const listEl = event.target.querySelector(SELECTOR_LIST);
    if (listEl && listEl.__stickyCardsListInstance) {
      listEl.__stickyCardsListInstance.destroy();
    }
  });
})();